/**
 * AuditStackedBarChart — SVG stacked bar chart for BDR activity volume.
 *
 * Series: Calls (navy) · Emails (slate) · Conversations (red) · Meetings (amber)
 * X-axis: time labels appropriate to the window
 * Y-axis: activity count
 *
 * Pure SVG — no external chart library required.
 */
import { tokens } from "@/components/operator/tokens";

interface DataPoint {
  label: string;
  calls: number;
  emails: number;
  convos: number;
  meetings: number;
}

interface Props {
  data: DataPoint[];
  title: string;
  /** BUILD-14: optional click handler. Receives series key + x-axis label. */
  onBarClick?: (series: "calls" | "emails" | "convos" | "meetings", label: string) => void;
}

const SERIES = [
  { key: "calls" as const,    label: "Calls",         color: tokens.color.navy },
  { key: "emails" as const,   label: "Emails",        color: "#6B6B70" },
  { key: "convos" as const,   label: "Conversations", color: tokens.color.red },
  { key: "meetings" as const, label: "Meetings",      color: "#EAB308" },
];

export default function AuditStackedBarChart({ data, title, onBarClick }: Props) {
  const W = 680;
  const H = 200;
  const PAD_LEFT = 36;
  const PAD_RIGHT = 16;
  const PAD_TOP = 16;
  const PAD_BOT = 32;
  const chartW = W - PAD_LEFT - PAD_RIGHT;
  const chartH = H - PAD_TOP - PAD_BOT;

  const maxTotal = Math.max(
    1,
    ...data.map((d) => d.calls + d.emails + d.convos + d.meetings)
  );
  // Y-ticks: 0, 25%, 50%, 75%, 100% of maxTotal rounded up
  const yMax = Math.ceil(maxTotal / 5) * 5 || 5;
  const yTicks = [0, Math.round(yMax * 0.25), Math.round(yMax * 0.5), Math.round(yMax * 0.75), yMax];

  const barW = Math.min(52, (chartW / data.length) * 0.6);
  const gap = chartW / data.length;

  function yPos(val: number) {
    return PAD_TOP + chartH - (val / yMax) * chartH;
  }

  if (data.length === 0) {
    return (
      <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", color: tokens.color.textSubtle, fontSize: 13 }}>
        No activity logged for this period.
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: tokens.color.textPrimary, marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block", minWidth: 320 }}>
          {/* Y-axis grid lines and labels */}
          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD_LEFT}
                x2={W - PAD_RIGHT}
                y1={yPos(tick)}
                y2={yPos(tick)}
                stroke="#EFEFEE"
                strokeWidth={1}
              />
              <text
                x={PAD_LEFT - 6}
                y={yPos(tick) + 4}
                textAnchor="end"
                fontSize={9}
                fill={tokens.color.textSubtle}
                fontFamily={tokens.font.body}
              >
                {tick}
              </text>
            </g>
          ))}

          {/* Bars */}
          {data.map((d, i) => {
            const cx = PAD_LEFT + i * gap + gap / 2;
            const x = cx - barW / 2;
            let bottom = PAD_TOP + chartH;
            return (
              <g key={d.label}>
                {SERIES.map(({ key, color }) => {
                  const val = d[key];
                  if (val === 0) return null;
                  const h = (val / yMax) * chartH;
                  bottom -= h;
                  return (
                    <rect
                      key={key}
                      x={x}
                      y={bottom}
                      width={barW}
                      height={h}
                      fill={color}
                      rx={key === SERIES[SERIES.length - 1].key ? 2 : 0}
                      style={{ cursor: onBarClick ? "pointer" : undefined }}
                      onClick={onBarClick ? () => onBarClick(key, d.label) : undefined}
                    >
                      <title>{`${d.label} — ${key}: ${val}${onBarClick ? " (click to drill down)" : ""}`}</title>
                    </rect>
                  );
                }).reverse()}
                {/* X-axis label */}
                <text
                  x={cx}
                  y={PAD_TOP + chartH + 18}
                  textAnchor="middle"
                  fontSize={10}
                  fill={tokens.color.textMuted}
                  fontFamily={tokens.font.body}
                >
                  {d.label}
                </text>
              </g>
            );
          })}

          {/* X axis line */}
          <line
            x1={PAD_LEFT}
            x2={W - PAD_RIGHT}
            y1={PAD_TOP + chartH}
            y2={PAD_TOP + chartH}
            stroke={tokens.color.border}
            strokeWidth={1}
          />
        </svg>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8 }}>
        {SERIES.map(({ key, label, color }) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: tokens.color.textMuted }}>
            <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: "inline-block" }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}
